import "./CmsLandingPage.css";
import { trackPublicPage } from "../services/visitor-monitoring";
import {
  Box,
  Zoom,
  useScrollTrigger,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  IconButton,
  Popper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import stegLogo from "../../assets/images/logo.png";

import {
  RootURL,
  Site,
  getPublicSiteNavigation,
  getPublicSites,
} from "../services/cms-api";

const isDraftPreview = window.location.hostname === "beteiligung.steg-gis.de";

const isVisible = (site: Site) =>
  site.isActive !== false &&
  site.is_active !== false &&
  (isDraftPreview ||
    (site.isPublished !== false && site.is_published !== false));

const isParticipationSite = (site: Site) =>
  String((site as any).instanceName || (site as any).instance_name || "") !==
  "die_steg";

const siteIdentifier = (site: Site) =>
  String(
    site.projektId ||
      site.project_nummer ||
      site.projectNummer ||
      site.urlKey ||
      site.id
  );

const projectNumberFor = (site: Site) => {
  const rawSite = site as any;
  return (
    site.project_nummer ||
    site.projectNummer ||
    rawSite.project?.nummer ||
    rawSite.project?.id ||
    site.projecktId ||
    site.projektId
  );
};

const municipalityNameFor = (site: Site) => {
  const rawSite = site as any;
  return (
    site.gemeindeName ||
    rawSite.gemeinde_name ||
    rawSite.gemeindeName ||
    rawSite.project?.gemeinde?.name ||
    rawSite.project?.gemeinde?.bezeichnung ||
    rawSite.project?.gemeinde_name
  );
};

const publicProjectPath = (site: Site, firstTab?: string) => {
  const projectNumber = projectNumberFor(site);
  const rawSite = site as any;
  const tenant =
    site.schemaName ||
    rawSite.schema_name ||
    rawSite.project?.gemeinde?.schema_name ||
    rawSite.project?.gemeinde?.schemaName ||
    site.instanceName ||
    site.urlKey;

  // CMS sites are published under the established BUBE-compatible address,
  // e.g. /dornhan/11944/startseite. `urlKey` is only an internal CMS alias.
  if (tenant && projectNumber) {
    return `/${encodeURIComponent(tenant)}/${encodeURIComponent(
      String(projectNumber)
    )}/${encodeURIComponent(firstTab || "startseite")}`;
  }

  return `/${encodeURIComponent(tenant)}/${encodeURIComponent(
    site.urlKey || siteIdentifier(site)
  )}`;
};

const imageUrl = (url?: string) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const host = RootURL.replace(/\/cms\/$/, "");
  return `${host}${url.startsWith("/") ? url : `/${url}`}`;
};

const TownDropdownPopper = (props: React.ComponentProps<typeof Popper>) => (
  <Popper
    {...props}
    placement="bottom-start"
    modifiers={{ flip: { enabled: false }, preventOverflow: { enabled: true, boundariesElement: "viewport" } }}
  />
);

export default function CmsLandingPage() {
  const location = useLocation();
  useEffect(() => { trackPublicPage(null, String(location.key || "") + location.pathname); }, [location.key, location.pathname]);
  const theme = useTheme();
  const showBackToTop = useScrollTrigger({ disableHysteresis: true, threshold: 300 });
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const isCompact = useMediaQuery(theme.breakpoints.down("sm"));
  const [sites, setSites] = useState<Site[]>([]);
  const [firstTabs, setFirstTabs] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTown, setSelectedTown] = useState("");
  const [townMenuOpen, setTownMenuOpen] = useState(false);
  const [townMenuClosing, setTownMenuClosing] = useState(false);
  const townCloseTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(townCloseTimer.current), []);
  const openTownMenu = () => {
    window.clearTimeout(townCloseTimer.current);
    setTownMenuClosing(false);
    setTownMenuOpen(true);
  };
  const closeTownMenu = () => {
    window.clearTimeout(townCloseTimer.current);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setTownMenuOpen(false);
      setTownMenuClosing(false);
      return;
    }
    setTownMenuClosing(true);
    townCloseTimer.current = window.setTimeout(() => {
      setTownMenuOpen(false);
      setTownMenuClosing(false);
    }, 140);
  };

  const filteredSites = useMemo(() => sites.filter((site) => {
    if (selectedTown && municipalityNameFor(site) !== selectedTown) return false;
    const queryTerms = searchQuery
      .trim()
      .toLocaleLowerCase("de-DE")
      .split(/\s+/)
      .filter(Boolean);
    const rawSite = site as any;
    const searchableText = [
      site.title,
      site.description,
      site.gemeindeName,
      rawSite.gemeinde_name,
      site.schemaName,
      site.urlKey,
    ]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase("de-DE");
    // Treat each word independently, so "Gaisburg Stuttgart" finds a
    // project even though those terms are not adjacent in its title.
    return queryTerms.every((term) => searchableText.includes(term));
  }), [sites, selectedTown, searchQuery]);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const previousPositions = useRef(new Map<string, DOMRect>());
  const previousHeight = useRef<number | null>(null);
  const resultAnimations = useRef<Animation[]>([]);
  const captureResultPositions = () => {
    const results = resultsRef.current;
    if (!results) return;
    previousPositions.current = new Map(
      Array.from(results.querySelectorAll<HTMLElement>("[data-project-id]")).map((element) =>
        [element.dataset.projectId!, element.getBoundingClientRect()] as [string, DOMRect])
    );
    previousHeight.current = results.getBoundingClientRect().height;
  };
  const updateSearch = (query: string) => {
    captureResultPositions();
    setSearchQuery(query);
  };
  const updateTown = (town: string) => {
    captureResultPositions();
    setSelectedTown(town);
  };
  useLayoutEffect(() => {
    const results = resultsRef.current;
    resultAnimations.current.forEach((animation) => animation.cancel());
    resultAnimations.current = [];
    if (!results || typeof results.animate !== "function" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timing = { duration: 420, easing: "cubic-bezier(.22, 1, .36, 1)" };
    const elements = Array.from(results.querySelectorAll<HTMLElement>("[data-project-id]"));
    const positions = elements.map((element) => ({ element, rect: element.getBoundingClientRect() }));
    const height = results.getBoundingClientRect().height;
    positions.forEach(({ element, rect }) => {
      const previous = previousPositions.current.get(element.dataset.projectId!);
      if (previous) {
        const x = previous.left - rect.left;
        const y = previous.top - rect.top;
        if (Math.abs(x) > 1 || Math.abs(y) > 1) {
          resultAnimations.current.push(element.animate([
            { transform: `translate(${x}px, ${y}px)` },
            { transform: "translate(0, 0)" },
          ], timing));
        }
      } else {
        resultAnimations.current.push(element.animate([
          { opacity: 0, transform: "translateY(12px) scale(.985)" },
          { opacity: 1, transform: "translateY(0) scale(1)" },
        ], timing));
      }
    });
    if (previousHeight.current !== null && Math.abs(previousHeight.current - height) > 1) {
      resultAnimations.current.push(results.animate([
        { height: `${previousHeight.current}px` }, { height: `${height}px` },
      ], timing));
    }
    previousPositions.current = new Map(positions.map(({ element, rect }) => [element.dataset.projectId!, rect]));
    previousHeight.current = height;
  }, [filteredSites, loading]);
  useEffect(() => () => resultAnimations.current.forEach((animation) => animation.cancel()), []);
  const isSearching = Boolean(searchQuery.trim() || selectedTown);
  useEffect(() => {
    const loadSites = async () => {
      try {
        const activeSites = (await getPublicSites()).filter(
          (site) => isVisible(site) && isParticipationSite(site)
        );
        setSites(activeSites);
        const tabs = await Promise.all(
          activeSites.map(async (site) => {
            const menu = await getPublicSiteNavigation(site.id);
            const firstVisibleTab = menu
              .filter(
                (item) => item.isActive !== false && item.isVisible !== false
              )
              .sort((a, b) => a.order - b.order)[0];
            return [site.id, firstVisibleTab?.urlKey] as const;
          })
        );
        setFirstTabs(
          tabs.reduce<Record<number, string>>((result, [siteId, tab]) => {
            if (tab) result[siteId] = tab;
            return result;
          }, {})
        );
      } catch (_error) {
        setSites([]);
      } finally {
        setLoading(false);
      }
    };
    loadSites();
  }, []);

  const municipalities = Array.from(new Set(sites.map(municipalityNameFor).filter((name): name is string => Boolean(name)))).sort((a, b) => a.localeCompare(b, "de"));
  const brand = { main: "#00B6E4", light: "#59CFEF", pale: "#BFECF9", dark: "#007F9E", text: "#1F2937", muted: "#6B7280", border: "#D8DEE6" };
  const scrollToSection = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const hash = event.currentTarget.hash;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });
    window.history.replaceState(window.history.state, "", hash);
  };
  const linkStyle = { color: "inherit", textUnderlineOffset: "4px" };

  return (
    <Box className="steg-public-landing" lang="de" style={{ minHeight: "100vh", background: "#F8FAFC", color: brand.text }}>
      <Box component="header" style={{ background: "#fff", borderBottom: `1px solid ${brand.border}` }}>
        <Container maxWidth="lg">
          <Box className="steg-public-masthead">
            <Box className="steg-public-identity">
              <img src={stegLogo} alt="die STEG" style={{ width: 145, height: "auto", display: "block" }} />
              <Typography variant="body2" style={{ color: brand.muted }}>Beteiligungsportal</Typography>
            </Box>
            <nav className="steg-public-nav" aria-label="Seitennavigation">
              <a href="#projekte" onClick={scrollToSection}>Projekte</a>
              <a href="#ueber-uns" onClick={scrollToSection}>So funktioniert’s</a>
            </nav>
          </Box>
        </Container>
      </Box>

      <Box className="steg-public-hero" style={{ background: "#fff", color: brand.text, borderTop: `3px solid ${brand.main}` }}>
        <Container maxWidth="lg">
          <Box className="steg-public-editorial">
            <Box>
              <Typography className="steg-public-eyebrow">GEMEINSAM VOR ORT</Typography>
              <Typography component="h1" className="steg-public-headline" style={{ color: brand.main }}>Mitgestalten. Mitreden.<br />Informiert bleiben.</Typography>
              <Typography className="steg-public-intro">Entdecken Sie, was sich in Ihrer Gemeinde bewegt. Teilen Sie Ihre Ideen und gestalten Sie die Zukunft Ihres Ortes mit.</Typography>
              <a className="steg-public-project-link" href="#projekte" onClick={scrollToSection}>Beteiligung entdecken <ArrowForwardIcon fontSize="small" /></a>
            </Box>
            <Box component="aside" className="steg-public-guide" aria-label="Ihre Möglichkeiten zur Beteiligung">
              <Typography className="steg-public-eyebrow">IHRE PERSPEKTIVE ZÄHLT</Typography>
              {[["Informieren", "Verstehen, was vor Ort geplant ist."], ["Mitreden", "Eigene Ideen und Perspektiven teilen."], ["Mitgestalten", "Gemeinsam die Zukunft Ihres Ortes bewegen."]].map(([title, description]) => <Box key={title} className="steg-public-guide-item">
                <Typography component="h2">{title}</Typography>
                <Typography>{description}</Typography>
              </Box>)}
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" style={{ paddingTop: 28, paddingBottom: 36 }}>
        <Box id="projekte" display="flex" alignItems="baseline" justifyContent="space-between" gridGap={16} mb={2}>
          <Typography component="h2" style={{ fontSize: 30, fontWeight: 400 }}>{isSearching ? "Suchergebnisse" : "Projekte in Ihrer Gemeinde"}</Typography>
        </Box>
        <Box className="steg-public-discovery">
          <TextField
            className="steg-public-search"
            variant="outlined"
            label="Projekt suchen"
            placeholder="Projektname oder Suchbegriff"
            value={searchQuery}
            onChange={(event) => updateSearch(event.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: brand.dark }} /></InputAdornment>,
              endAdornment: searchQuery ? <InputAdornment position="end"><IconButton size="small" aria-label="Projektsuche zurücksetzen" onClick={() => updateSearch("")}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : undefined,
            }}
          />
          <Autocomplete
            className="steg-public-town-select"
            PopperComponent={TownDropdownPopper}
            classes={{ paper: `steg-public-town-menu${townMenuClosing ? " steg-public-town-menu-closing" : ""}` }}
            open={townMenuOpen}
            onOpen={openTownMenu}
            onClose={closeTownMenu}
            options={municipalities}
            value={selectedTown || null}
            onChange={(_event, town) => updateTown(town || "")}
            autoHighlight
            openOnFocus
            clearText="Gemeinde zurücksetzen"
            openText="Gemeinden anzeigen"
            closeText="Gemeinden schließen"
            noOptionsText="Keine Gemeinde gefunden"
            renderInput={(params) => <TextField {...params} label="Gemeinde auswählen" placeholder="Alle Gemeinden" variant="outlined" InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
          />
        </Box>
        <div ref={resultsRef} className="steg-public-results" aria-busy={loading}>
        <Box>
        {loading ? <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box> : <Grid container spacing={3}>
          {filteredSites.map((site) => <Grid data-project-id={site.id} item xs={12} sm={6} md={4} key={site.id} style={{ display: "flex" }}>
            <Link className="steg-public-card" to={publicProjectPath(site, firstTabs[site.id])} style={{ display: "flex", flexDirection: "column", width: "100%", textDecoration: "none", color: "inherit", background: "#fff", border: `1px solid ${brand.border}`, borderRadius: 12, overflow: "hidden" }}>
              {site.headerImage ? <img src={imageUrl(site.headerImage)} alt="" loading="lazy" style={{ width: "100%", height: 190, objectFit: "cover", display: "block" }} /> : <Box style={{ height: 8, background: brand.main }} />}
              <Box style={{ padding: 22, display: "flex", flexDirection: "column", flex: 1 }}>
                <Typography component="h3" className="steg-public-town-name" style={{ fontSize: 28, fontWeight: 400, color: brand.main, lineHeight: 1.2, marginBottom: 10 }}>{municipalityNameFor(site) || "Beteiligungsprojekt"}</Typography>
                <Typography className="steg-public-project-title" style={{ fontSize: 16, lineHeight: 1.5, color: brand.muted, fontWeight: 400, marginBottom: 20 }}>{site.title}</Typography>
                {site.description && <Typography style={{ color: brand.muted, fontSize: 14, lineHeight: 1.6, marginTop: 0, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden", marginBottom: 20 }}>{site.description}</Typography>}
                <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginTop: "auto", paddingTop: 18, borderTop: `1px solid ${brand.pale}`, color: brand.dark, fontWeight: 700, fontSize: 14 }}>Projekt ansehen<ArrowForwardIcon fontSize="small" /></Box>
              </Box>
            </Link>
          </Grid>)}
        </Grid>}
        {!loading && !filteredSites.length && <Box py={4}><Typography>{isSearching ? "Keine passenden Projekte gefunden. Versuchen Sie einen anderen Ort oder Suchbegriff." : "Derzeit sind keine Projekte veröffentlicht."}</Typography></Box>}

        </Box>
        </div>

        <Box id="ueber-uns" style={{ marginTop: 36, paddingTop: 28, borderTop: `1px solid ${brand.border}` }}>
          <Typography component="h2" style={{ fontSize: 28, fontWeight: 400, marginBottom: 20 }}>So können Sie sich beteiligen</Typography>
          <Grid container spacing={3}>
            {[["01", "Informieren", "Erfahren Sie, was geplant ist und wie sich Ihr Ort entwickelt."], ["02", "Mitreden", "Teilen Sie Ihre Ideen und Perspektiven im jeweiligen Projekt."], ["03", "Mitgestalten", "Verfolgen Sie Ergebnisse und die nächsten Schritte vor Ort."]].map(([number, title, description]) => <Grid item xs={12} sm={4} key={number}>
              <Box display="flex" gridGap={14}><Typography style={{ color: brand.dark, fontWeight: 700, fontSize: 14, flexShrink: 0, paddingTop: 3 }}>{number}</Typography><Box><Typography component="h3" style={{ fontWeight: 400, fontSize: 23 }}>{title}</Typography><Typography style={{ color: brand.muted, fontSize: 14, lineHeight: 1.6, marginTop: 5 }}>{description}</Typography></Box></Box>
            </Grid>)}
          </Grid>
        </Box>
      </Container>
      <Box component="footer" style={{ background: "#fff", borderTop: `1px solid ${brand.border}`, padding: "22px 0", fontSize: 13, color: brand.muted }}>
        <Container maxWidth="lg">
          <Box display="flex" flexDirection={isCompact ? "column" : "row"} justifyContent="space-between" gridGap={16}>
            <span>© {new Date().getFullYear()} die STEG Stadtentwicklung GmbH</span>
            <Box display="flex" flexWrap="wrap" gridGap={22}>
              <a href="mailto:beteiligung@steg.de" style={linkStyle}>Kontakt</a>
              <a href="https://www.steg.de/impressum/" style={linkStyle}>Impressum</a>
              <a href="https://www.steg.de/datenschutzerklaerung/" style={linkStyle}>Datenschutz</a>
            </Box>
          </Box>
        </Container>
      </Box>
      <Zoom in={showBackToTop} timeout={reduceMotion ? 0 : 180} unmountOnExit>
        <IconButton
          className="steg-public-back-to-top"
          title="Nach oben"
          onClick={() => {
            window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
            window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
          }}
          aria-label="Zurück zum Seitenanfang"
        ><ArrowUpwardIcon fontSize="small" /></IconButton>
      </Zoom>
    </Box>
  );
}
